import type { PartnerAccountant } from '../../models/partnerAccountant.ts';
import type { UpdatePartnerAccountantForm } from '../../components/modal/partner-accountants/UpdatePartnerAccountantModal.tsx';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { Box, ActionIcon } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import useUpdatePartnerAccountant from '../../hooks/partner-accountants/useUpdatePartnerAccountant.ts';
import useVoidPartnerAccountant from '../../hooks/partner-accountants/useVoidPartnerAccountant.ts';
import { useQueryClient } from '@tanstack/react-query';
import { notifySuccess } from '../../utils/NotifySuccess.tsx';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import UpdatePartnerAccountantModal from '../modal/partner-accountants/UpdatePartnerAccountantModal.tsx';

interface PartnerAccountantTableProps {
  partnerId: string;
  accountants: PartnerAccountant[];
  isLoading: boolean;
  hidden: boolean;
}

export default function PartnerAccountantTable({
  partnerId,
  accountants,
  isLoading,
  hidden,
}: PartnerAccountantTableProps) {
  const queryClient = useQueryClient();
  const records: PartnerAccountant[] = accountants;
  const {
    mutate: updatePartnerAccountant,
    isPending: updatePartnerAccountantIsPending,
  } = useUpdatePartnerAccountant();
  const {
    mutate: voidPartnerAccountant,
    isPending: voidPartnerAccountantIsPending,
  } = useVoidPartnerAccountant();

  const [selectedAccountant, setSelectedAccountant] =
    useState<PartnerAccountant | null>(null);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);

  const columns: DataTableColumn<PartnerAccountant>[] = [
    {
      title: '人員名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
      width: 480,
    },
    {
      title: 'Email',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'email',
    },
    {
      accessor: '',
      titleStyle: { color: 'gray', fontWeight: 'normal', textAlign: 'center' },
      width: 120,
      textAlign: 'right',
      render: (row) => (
        <span style={{ paddingRight: '20px' }}>
          <EditButton row={row} onClickFunction={onClickEditButton} />
          <DeleteButton row={row} onClickFunction={onClickDeleteButton} />
        </span>
      ),
    },
  ];

  function onClickEditButton(row: PartnerAccountant) {
    setSelectedAccountant(row);
    openEditModal();
  }

  function onConfirmUpdatePartnerAccountant(
    partnerAccountantId: string,
    formValues: UpdatePartnerAccountantForm,
  ) {
    if (updatePartnerAccountantIsPending) return;

    updatePartnerAccountant(
      { id: partnerAccountantId, ...formValues },
      {
        onSuccess: () => {
          closeEditModal();
          notifySuccess('更新會計人員成功');
          queryClient.invalidateQueries({
            queryKey: ['partnerAccountants', partnerId],
          });
        },
      },
    );
  }

  function onClickDeleteButton(row: PartnerAccountant) {
    modals.openContextModal({
      modal: 'confirmModal',
      title: '刪除會計人員',
      closeOnEscape: true,
      closeOnClickOutside: true,
      innerProps: {
        modalBody: `確定要刪除該會計人員(${row.email})嗎?`,
        loading: voidPartnerAccountantIsPending,
        onConfirm: () => onConfirmDeletePartnerAccountant(partnerId, row.id),
      },
    });
  }

  function onConfirmDeletePartnerAccountant(
    partnerId: string,
    partnerAccountantId: string,
  ) {
    if (voidPartnerAccountantIsPending) return;

    voidPartnerAccountant(partnerAccountantId, {
      onSuccess: () => {
        modals.closeAll();
        notifySuccess('刪除會計人員成功');
        queryClient.invalidateQueries({
          queryKey: ['partnerAccountants', partnerId],
        });
      },
    });
  }

  return (
    <>
      {selectedAccountant && editModalOpened && (
        <UpdatePartnerAccountantModal
          opened={editModalOpened}
          onClose={closeEditModal}
          row={selectedAccountant}
          isPending={updatePartnerAccountantIsPending}
          onConfirm={({ formValues }) =>
            onConfirmUpdatePartnerAccountant(selectedAccountant.id, formValues)
          }
        />
      )}
      <Box hidden={hidden}>
        <DataTable
          idAccessor="id"
          highlightOnHover
          minHeight={150}
          columns={columns}
          records={records}
          verticalSpacing="sm"
          noRecordsText="目前還沒有任何人員，點擊「新增人員」開始建立！"
          fetching={isLoading}
        />
      </Box>
    </>
  );
}

function EditButton({
  row,
  onClickFunction,
}: {
  row: PartnerAccountant;
  onClickFunction: (row: PartnerAccountant) => void;
}) {
  return (
    <ActionIcon
      variant="transparent"
      size="sm"
      ml="xs"
      onClick={() => onClickFunction(row)}
    >
      <IconEdit size={16} color={'#141414'} />
    </ActionIcon>
  );
}

function DeleteButton({
  row,
  onClickFunction,
}: {
  row: PartnerAccountant;
  onClickFunction: (row: PartnerAccountant) => void;
}) {
  return (
    <ActionIcon
      variant="transparent"
      size="sm"
      ml="xs"
      onClick={() => onClickFunction(row)}
    >
      <IconTrash size={16} color={'#FA5252'} />
    </ActionIcon>
  );
}
